import type { ParsedApiError } from '@loctopus/shared';
import { cn } from '../utils/cn';

export interface ErrorSummaryProps {
  error: ParsedApiError;
  className?: string;
}

/**
 * The shared error display component (used by apps/admin, apps/dashboard, apps/scanner,
 * apps/checkout alongside `@loctopus/shared`'s `parseApiError`). Renders the category title, the
 * message, and (for `permission` errors) which permission was missing - plus the request ID so
 * the user can quote it back, per the hard rule that a failure must always say what happened.
 *
 * Does NOT render field-level validation messages - those go next to each input via
 * `fieldError(parsed, fieldName)` passed into `<FormField error={...}>` (packages/ui), not
 * repeated here. For a `reauth` kind error, prefer showing the shared reauth dialog instead of
 * this banner (apps/dashboard's `useReauth`/apps/admin's equivalent) - this still renders a
 * reasonable fallback message for any action that doesn't have that wired up yet.
 */
export function ErrorSummary({ error, className }: ErrorSummaryProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col gap-1 rounded-md border border-coral-300 bg-coral-50 px-3 py-2.5 text-sm text-coral-900 dark:border-coral-700 dark:bg-coral-950/40 dark:text-coral-100',
        className,
      )}
    >
      <p className="font-medium">{error.title}</p>
      <p>{error.message}</p>
      {error.missingPermission && (
        <p className="text-coral-700 dark:text-coral-300">
          Missing permission: <code className="font-mono">{error.missingPermission}</code>
        </p>
      )}
      {error.requestId && (
        <p className="text-xs text-coral-600 dark:text-coral-400">
          Request ID: <code className="font-mono">{error.requestId}</code> (quote this if you
          report the problem)
        </p>
      )}
    </div>
  );
}
